// Admin log tail viewer — polls /api/admin/logs/tail and renders a
// newest-first list of structured JSON rows with level/logger/free-text
// filters, auto-refresh, and an expandable JSON drawer per row.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import { FileText, RefreshCw, ChevronRight, Filter, Play, Pause } from 'lucide-react';

const LEVELS = [
  { value: 'all',       label: 'All levels' },
  { value: 'ERROR',     label: 'Error only' },
  { value: 'WARNING',   label: 'Warning' },
  { value: 'INFO',      label: 'Info' },
  { value: 'CRITICAL',  label: 'Critical' },
];

const LEVEL_STYLES = {
  ERROR:    'border-red-900 text-red-300 bg-red-950/40',
  CRITICAL: 'border-red-900 text-red-100 bg-red-900/60',
  WARNING:  'border-amber-800 text-amber-300 bg-amber-950/40',
  INFO:     'border-cyan-900 text-cyan-300 bg-cyan-950/40',
  DEBUG:    'border-slate-700 text-slate-400 bg-slate-800/40',
};

function LogRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const cls = LEVEL_STYLES[(row.level || '').toUpperCase()] || LEVEL_STYLES.INFO;
  const ts = row.ts?.replace('T', ' ').replace('+0000', 'Z') || '';
  return (
    <div className="border border-slate-800 rounded-md bg-slate-900/40 hover:bg-slate-900/70 transition-colors" data-testid="log-row">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-3 p-2.5 text-left"
      >
        <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform shrink-0 ${expanded ? 'rotate-90' : ''}`} />
        <Badge variant="outline" className={`${cls} shrink-0 w-[76px] justify-center`}>{row.level || 'INFO'}</Badge>
        <span className="text-slate-500 text-[11px] font-mono shrink-0 hidden md:inline">{ts}</span>
        <span className="text-slate-500 text-[11px] font-mono shrink-0 hidden lg:inline truncate max-w-[200px]">{row.logger || ''}</span>
        <span className="text-slate-100 text-sm truncate flex-1 min-w-0">
          {row.msg || row.exc?.split('\n')[0] || '(no message)'}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-slate-800 p-3 bg-slate-950/60">
          <pre className="text-[11px] text-slate-300 font-mono whitespace-pre-wrap break-all overflow-x-auto">
            {JSON.stringify(row, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default function AdminLogsPage() {
  const { api } = useAuth();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ scanned: 0, matched: 0 });
  const [level, setLevel] = useState('ERROR');   // Default: ERROR-only to match user intent.
  const [contains, setContains] = useState('');
  const [loggerName, setLoggerName] = useState('');
  const [limit, setLimit] = useState(200);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const timerRef = useRef(null);

  const params = useMemo(() => {
    const p = { limit };
    if (level && level !== 'all') p.level = level;
    if (contains.trim()) p.contains = contains.trim();
    if (loggerName.trim()) p.logger_name = loggerName.trim();
    return p;
  }, [level, contains, loggerName, limit]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/logs/tail', { params });
      setRows(data.rows || []);
      setMeta({ scanned: data.scanned, matched: data.matched });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [api, params]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh loop
  useEffect(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoRefresh) {
      timerRef.current = setInterval(load, 5000);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [autoRefresh, load]);

  return (
    <div className="space-y-6" data-testid="admin-logs-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-white text-2xl font-semibold flex items-center gap-2">
            <FileText className="w-6 h-6 text-cyan-400" /> Structured Log Viewer
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Tailing <code className="text-slate-400">/var/log/aeroguard/backend.jsonl</code>.
            {meta.scanned > 0 && <span className="ml-2">Scanned {meta.scanned} lines · {meta.matched} matched.</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button onClick={() => setAutoRefresh((v) => !v)} variant="outline"
            className={`border-slate-700 ${autoRefresh ? 'text-emerald-300 bg-emerald-950/40' : 'text-slate-300 bg-slate-900/40'}`}
            data-testid="logs-auto-refresh-btn">
            {autoRefresh ? <Pause className="w-3.5 h-3.5 mr-1.5" /> : <Play className="w-3.5 h-3.5 mr-1.5" />}
            {autoRefresh ? 'Auto (5s)' : 'Auto off'}
          </Button>
          <Button onClick={load} variant="ghost" disabled={loading}
            className="text-slate-400 hover:text-slate-200"
            data-testid="logs-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base flex items-center gap-2">
            <Filter className="w-4 h-4 text-cyan-400" /> Filters
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <div>
            <Label className="text-slate-300 text-xs">Level</Label>
            <Select value={level} onValueChange={setLevel}>
              <SelectTrigger className="bg-slate-950 border-slate-800 mt-1.5" data-testid="logs-level-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {LEVELS.map((l) => (
                  <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Logger contains</Label>
            <Input
              value={loggerName}
              onChange={(e) => setLoggerName(e.target.value)}
              className="bg-slate-950 border-slate-800 mt-1.5 font-mono text-xs"
              placeholder="client_errors"
              data-testid="logs-logger-input"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Text contains</Label>
            <Input
              value={contains}
              onChange={(e) => setContains(e.target.value)}
              className="bg-slate-950 border-slate-800 mt-1.5"
              placeholder="e.g. TypeError"
              data-testid="logs-text-input"
            />
          </div>
          <div>
            <Label className="text-slate-300 text-xs">Limit</Label>
            <Select value={String(limit)} onValueChange={(v) => setLimit(Number(v))}>
              <SelectTrigger className="bg-slate-950 border-slate-800 mt-1.5" data-testid="logs-limit-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                {[50, 100, 200, 500, 1000].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} rows</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-slate-900/60 border-slate-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">
            {rows.length} row{rows.length === 1 ? '' : 's'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {rows.length === 0 && !loading && (
            <div className="text-slate-500 text-sm py-8 text-center">
              No log rows match the current filters. Try clearing the text search or lowering the level to <em>All</em>.
            </div>
          )}
          {rows.map((row, i) => (
            <LogRow key={`${row.ts}-${i}`} row={row} />
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
